import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { TransactionsPairForReconciliation } from "@common/contracts/reconciliation.contract";
import { useDndContext } from "@dnd-kit/core";
import { useVirtualizer } from "@tanstack/react-virtual";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { GridTable } from "@design-system/Tables/GridTable/GridTable";
import styles from "./Reconciliation.module.scss";
import { ReconciliationEmptyRow } from "./ReconciliationEmptyRow";
import { ReconciliationPair } from "./ReconciliationPair";
import {
    ReconciliationTableHeader,
    ReconciliationTableHeaderProps,
} from "./ReconciliationTableHeader";
import { ReconciliationContext } from "./Reconciliation.context";
import {
    APPROVE_RECONCILIATION_CHANGE_BUTTON_CLASSNAME,
    buildReconciliationTableSections,
    getReconciliationPairKey,
    RECONCILIATION_ROW_CLASSNAME,
    RECONCILIATION_ROW_HEIGHT_PX,
    ReconciliationPairKey,
} from "./helpers";

/**
 * Identifies a parent-driven request to scroll-and-focus a particular pair.
 * The `key` is a stable per-pair identity (avoids mismatches between the
 * parent's `pairs` ordering and the bucketed `flatPairs` ordering used for
 * rendering); `requestId` increments on every request so repeated clicks
 * targeting the same pair (e.g. one-pending-difference scenarios) still
 * trigger a scroll-and-focus.
 */
export interface FocusRequest {
    key: ReconciliationPairKey;
    requestId: number;
}

interface Props extends ReconciliationTableHeaderProps {
    pairs: TransactionsPairForReconciliation[];
    onUpdatePair: (pair: TransactionsPairForReconciliation) => void;
    isBalanceReconciliation: boolean;
    focusRequest?: FocusRequest;
}

const ROW_HEIGHT = RECONCILIATION_ROW_HEIGHT_PX;
const MIN_OVERSCAN_ROWS = 5;
// Recon doesn't use a `DragOverlay` — the dragged row IS the visual, so
// unmounting it mid-drag would kill the gesture. Generous headroom protects
// long drags past the viewport.
const DRAG_OVERSCAN_ROWS = 100;
// Approximate non-row chrome height inside the modal (page status bar +
// table header + footer). Used as a baseline subtraction so the
// viewport-derived overscan tracks the actual visible-row count.
const NON_ROW_CHROME_PX = 220;

function computeIdleOverscan(): number {
    // Half a viewport's worth of rows above and below — same heuristic as
    // `calculateInitialVisibleRows` in `ViewTable`.
    const viewportHeight = window?.innerHeight ?? 0;
    const visibleRows = Math.max(
        MIN_OVERSCAN_ROWS * 2,
        Math.ceil((viewportHeight - NON_ROW_CHROME_PX) / ROW_HEIGHT),
    );
    return Math.max(MIN_OVERSCAN_ROWS, Math.ceil(visibleRows / 2));
}

export const ReconciliationTransactionsTable: React.FC<Props> = ({
    pairs,
    openingDate,
    closingDate,
    onUpdatePair,
    isBalanceReconciliation,
    focusRequest,
    ...rest
}) => {
    const { financialTransactionsFromPreviousPeriodIds } = useContext(
        ReconciliationContext,
    );
    const { active: activeDndItem, over: overDndItem } = useDndContext();
    const activeData = activeDndItem?.data.current;
    const draggedAmount =
        typeof activeData?.amount === "number" ? activeData.amount : undefined;
    const draggedCashItemDate =
        activeData?.isCashItem === true && typeof activeData?.date === "string"
            ? activeData.date
            : undefined;
    const draggedId = activeDndItem?.id;
    const overItemId = overDndItem?.id;

    const { flatPairs, lastBeforeStatementIndex, lastInStatementIndex } =
        useMemo(
            () =>
                buildReconciliationTableSections({
                    pairs,
                    openingDate,
                    closingDate,
                    financialTransactionsFromPreviousPeriodIds,
                }),
            [
                pairs,
                openingDate,
                closingDate,
                financialTransactionsFromPreviousPeriodIds,
            ],
        );

    const focusedFlatIndex = useMemo(() => {
        if (focusRequest == null) {
            return -1;
        }
        return flatPairs.findIndex(
            (pair) => getReconciliationPairKey(pair) === focusRequest.key,
        );
    }, [flatPairs, focusRequest]);

    const tableContainerRef = useRef<HTMLDivElement>(null);

    // Memoized with `[]`: window resize triggers its own re-render via the
    // modal's full-height parent, which gives the virtualizer a chance to
    // recompute the visible window.
    const idleOverscan = useMemo(() => computeIdleOverscan(), []);

    // oxlint-disable-next-line react/incompatible-library -- intended skip: rows get plain props and a ref-backed measureElement
    const rowVirtualizer = useVirtualizer({
        count: flatPairs.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: activeDndItem ? DRAG_OVERSCAN_ROWS : idleOverscan,
        getItemKey: (i) => getReconciliationPairKey(flatPairs[i]) ?? i,
        measureElement: (element, entry) => {
            const box = entry?.borderBoxSize?.[0];
            if (box) {
                return Math.round(box.blockSize);
            }
            return element?.getBoundingClientRect().height ?? 0;
        },
    });

    // Bring the focused row into the viewport. The virtualizer schedules its
    // own re-render to mount the row; the second effect below runs once that
    // commit lands and the row is in the DOM — keeping scroll and focus in
    // the same React commit so they paint together (no visible lag between
    // the view moving and the action button being highlighted).
    const focusRequestId = focusRequest?.requestId;
    useEffect(() => {
        if (focusedFlatIndex < 0 || focusRequestId == null) {
            return;
        }
        rowVirtualizer.scrollToIndex(focusedFlatIndex, {
            align: "center",
            behavior: "auto",
        });
    }, [focusRequestId, focusedFlatIndex, rowVirtualizer]);

    const virtualItems = rowVirtualizer.getVirtualItems();

    // Focus the action button as soon as the row is mounted. Re-runs on
    // `virtualItems` changes because `scrollToIndex` is asynchronous: the row
    // isn't in the DOM until the next React commit. `lastHandledRequestIdRef`
    // pins the focus to a single user request so subsequent re-renders from
    // unrelated state don't steal focus from the user. Tracking the request
    // id (rather than the key) lets the same pair receive focus again — for
    // example when there's only one pending difference and the user clicks
    // "Go to next difference" repeatedly.
    const lastHandledRequestIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (focusRequestId == null || focusedFlatIndex < 0) {
            return;
        }
        if (lastHandledRequestIdRef.current === focusRequestId) {
            return;
        }
        const isMounted = virtualItems.some(
            (item) => item.index === focusedFlatIndex,
        );
        if (!isMounted) {
            return;
        }
        const row = tableContainerRef.current?.querySelector(
            `.${RECONCILIATION_ROW_CLASSNAME}[data-index="${focusedFlatIndex}"]`,
        );
        // `preventScroll: true` keeps the browser from running its own
        // `scrollIntoView({ block: "nearest" })` on top of the virtualizer's
        // `scrollToIndex` above. Without it, the two scrolls fight on every
        // click and the focused row alternates between the virtualizer's
        // centered target and the browser's nearest-edge placement.
        row?.querySelector<HTMLButtonElement>(
            `.${APPROVE_RECONCILIATION_CHANGE_BUTTON_CLASSNAME}`,
        )?.focus({ preventScroll: true });
        lastHandledRequestIdRef.current = focusRequestId;
    }, [focusRequestId, focusedFlatIndex, virtualItems]);

    const totalSize = rowVirtualizer.getTotalSize();
    const topSpacerHeight = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const bottomSpacerHeight =
        virtualItems.length > 0
            ? totalSize - virtualItems[virtualItems.length - 1].end
            : 0;

    const isEmpty = flatPairs.length === 0;
    // Striped filler rows for the empty state so the table doesn't collapse
    // into the page status bar when there are no pairs to reconcile. Captured
    // once on mount — the modal is full-height and we don't need to react to
    // viewport resizes for filler aesthetics.
    const emptyFillerRowCountRef = useRef(
        Math.floor((window?.innerHeight ?? 0) / ROW_HEIGHT),
    );

    const measureRef = useRef(rowVirtualizer.measureElement);
    measureRef.current = rowVirtualizer.measureElement;
    const measureElement = useCallback(
        (el: HTMLElement | null) => measureRef.current(el),
        [],
    );

    const commonPairProps = {
        onUpdatePair,
        isBalanceReconciliation,
        draggedAmount,
        draggedCashItemDate,
        draggedId,
        overItemId,
        measureElement,
    };

    return (
        <div ref={tableContainerRef} className={styles.tableScrollContainer}>
            <GridTable noBorder className={styles.table}>
                <ReconciliationTableHeader
                    openingDate={openingDate}
                    closingDate={closingDate}
                    {...rest}
                />

                {topSpacerHeight > 0 && (
                    <div
                        className={styles.virtualSpacer}
                        style={{ height: topSpacerHeight }}
                        aria-hidden
                    />
                )}

                {virtualItems.map((virtualRow) => {
                    const pair = flatPairs[virtualRow.index];
                    if (!pair) {
                        return null;
                    }
                    return (
                        <ReconciliationPair
                            key={virtualRow.key}
                            pair={pair}
                            index={virtualRow.index}
                            isLastBeforeStatement={
                                virtualRow.index === lastBeforeStatementIndex
                            }
                            isLastInStatement={
                                virtualRow.index === lastInStatementIndex
                            }
                            {...commonPairProps}
                        />
                    );
                })}

                {bottomSpacerHeight > 0 && (
                    <div
                        className={styles.virtualSpacer}
                        style={{ height: bottomSpacerHeight }}
                        aria-hidden
                    />
                )}

                {isEmpty &&
                    Array.from({ length: emptyFillerRowCountRef.current }).map(
                        (_, index) => (
                            <ReconciliationEmptyRow
                                key={`recon-empty-row-${index}`}
                            />
                        ),
                    )}
            </GridTable>
        </div>
    );
};
